import Button from '@ingka/button';
import information from '@ingka/ssr-icon/paths/information';
import Tooltip from '@ingka/tooltip';
import { usePreviewEmailMutation } from '@lotta/self-service/lottery-fe/data-access-state';
import { getPreviewEmailContent } from '@lotta/self-service/lottery-fe/util-utilities';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import styles from '../../LotteryForm.module.scss';

export type SendPreviewEmailButtonControlType = {
  translation: Record<
    string,
    {
      subjectLine: string;
      preHeader: string;
      bodyHeadline: string;
      ctaText: string;
      ctaLink?: string;
      disclaimerText?: string;
      body: string;
      prizePolicyUrl: string;
    }
  >;
  bannerImageUrl: string;
};

export type SendPreviewEmailButtonProps = {
  retailUnit: string;
  language: string;
  control: Control<SendPreviewEmailButtonControlType>;
  overrideCtaLink?: string;
};

export const SendPreviewEmailButton: React.FC<SendPreviewEmailButtonProps> = ({
  retailUnit,
  language,
  control,
  overrideCtaLink,
}) => {
  const { t } = useTranslation();
  const [previewEmail] = usePreviewEmailMutation();
  const [disabled, setDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const translation = useWatch({
    control,
    name: 'translation',
  });
  const bannerImageUrl = useWatch({
    control,
    name: 'bannerImageUrl',
  });

  useEffect(() => {
    // The translation will initially be empty
    if (!(language in translation)) {
      setDisabled(true);
      return;
    }
    const languageSpecificFormFields = translation[language];
    const fieldsToCheck = { ...languageSpecificFormFields };
    delete fieldsToCheck.disclaimerText;

    const atLeastOneFieldEmpty = Object.values(fieldsToCheck).some(
      (value) => !value || value.length === 0
    );
    setDisabled(atLeastOneFieldEmpty);
  }, [translation, language]);

  const sendPreview = () => {
    const ctaLink = overrideCtaLink ?? translation[language].ctaLink ?? '';
    const formContent = { ...translation[language], ctaLink, bannerImageUrl };
    const emailContent = getPreviewEmailContent(
      formContent,
      language.toUpperCase(),
      retailUnit.toUpperCase()
    );
    previewEmail({ retailUnit, emailContent });
  };

  return (
    <div className={styles['preview-container']}>
      <div className={styles['preview-button-container']}>
        <Button
          text={`${t('lottery-form-email-form-send-preview')}`}
          type="primary"
          loading={isLoading}
          disabled={isLoading || disabled}
          onClick={() => {
            sendPreview();
            setIsLoading(true);
            setTimeout(() => {
              setIsLoading(false);
            }, 30000);
          }}
          data-testid="send-preview-email-button"
        />
      </div>
      <div className={styles['tool-tip-preview']}>
        <Tooltip
          tooltipText={t('lottery-form-email-form-send-preview-tool-tip')}
          ssrIcon={information}
          position="trailing"
        >
          <Button iconOnly ssrIcon={information} type="tertiary" small />
        </Tooltip>
      </div>
    </div>
  );
};
